import { Entity } from '@core/api/entity';
import {
  CompanyInvestorTypeDTO,
  CompanyReadDTO,
  CompanyTypeDTO,
  CompanyWriteDTO,
  DataroomOpenType,
} from '@core/dto/company.dto';
import { ContactTypeDTO } from '@core/dto/contact.dto';
import { ProductDTO } from '@core/dto/product.dto';
import { ValueObjectDTO } from '@core/dto/value-object.dto';
import { normalizeDate } from '@core/utils';
import { Model, Serializer } from '@junte/serialize-ts';
import { CompanyStatusDTO } from '../dto/company.dto';
import { ContactCopy } from './contacts-copy.model';
import { FileObject, FileObjectSerializer } from './file-object.model';
import { ValueObject, ValueObjectSerializer } from './value-object.model';
import { AssetClassValueObject, AssetClassValueObjectSerializer } from './asset-class-value-object';

export type CompanyInvestorType = CompanyInvestorTypeDTO;
export type CompanyType = CompanyTypeDTO;
export type CompanyStatus = CompanyStatusDTO;
export type { DataroomOpenType };

type ContactPrefix = 'benefOwner' | 'legalRepr';
type ContactSuffix =
  | 'FirstName'
  | 'IsPep'
  | 'LastName'
  | 'Address'
  | 'Email'
  | 'Mobile'
  | 'Phone'
  | 'Salutation'
  | 'Title'
  | 'Delete'
  | 'IdCardScan'
  | 'SignedDeclaration';

export class CompanySerializer implements Serializer<Company> {
  public serialize(model: Company): CompanyWriteDTO {
    const vos = new ValueObjectSerializer();

    const dto: CompanyWriteDTO = {
      address: model.address,
      articlesOfAssocOrContract: model.articlesOfAssociationOrSocialContract,
      bankAccountNumber: model.bankAccountNumber,
      benefOwnerIdCardScan: model.beneficialOwnerIdCardScan,
      financialAuthorityLicense2: model.financialAuthorityLicense2,
      benefOwnerNum: model.beneficialOwners?.length,
      benefOwnerSignedDeclaration: model.beneficialOwnerSignedDeclaration,
      commercialRegisterExtract: model.commercialRegisterExtract,
      country: model.country ? vos.serialize(model.country) : model.country,
      depotAccountNumber: model.depotAccountNumber,
      email: model.email,
      verificationFeedback: model.verificationFeedback,
      firstContactFirstName: model.firstContactFirstName,
      firstContactLastName: model.firstContactLastName,
      firstContactRole: model.firstContactRole,
      firstContactSalutation: model.firstContactSalutation
        ? vos.serialize(model.firstContactSalutation)
        : model.firstContactSalutation,
      firstContactTitle: model.firstContactTitle ? vos.serialize(model.firstContactTitle) : model.firstContactTitle,
      fullCompanyName: model.name,
      investorClasses: model.investorClasses
        ? model.investorClasses.map((v) => vos.serialize(v))
        : model.investorClasses,
      isManagingCompany: model.isManagingCompany,
      isVehicleCompany: model.isVehicleCompany,
      lastAnnualFinStatement: model.lastAnnualFinancialStatement,
      legalReprIdCardScan: model.legalRepresentativeIdCardScan,
      legalReprNum: model.legalRepresentatives?.length,
      legalReprSignedDeclaration: model.legalRepresentativeSignedDeclaration,
      lei: model.lei,
      mobile: model.mobile,
      proofNote: model.proofNote,
      phone: model.phone,
      proofDocument: model.proofDocument,
      provenAt: model.provenAt ? normalizeDate(model.provenAt) : null,
      provenBy: model.provenBy,
      relatedProducts: model.relatedProducts
        ? this.relatedProductsToValueObject(model.relatedProducts as Partial<ProductDTO>[]).map((v) => vos.serialize(v))
        : [],
      shortDescription: model.shortDescription,
      status: model.status,
      transparencyRegistry: model.transparencyRegister,
      structureChart: model.structureChart,
      bankStatement: model.bankStatement,
      type: model.type,
      uid: model.id,
      website: model.website,
      image: model.image,
      investorType: model.investorType,
      partner: model.partner,
      ownWhitelabel: model.ownWhitelabel,
      clientAdviceLicence: model.clientAdviceLicence,
      adviceFooterText: model.adviceFooterText,

      openProportionCommissionUpfront: model.openProportionCommissionUpfront,
      openProportionManagementFee: model.openProportionManagementFee,
      openProportionCarriedInterest: model.openProportionCarriedInterest,
      closedProportionCommissionUpfront: model.closedProportionCommissionUpfront,
      closedProportionManagementFee: model.closedProportionManagementFee,
      closedProportionCarriedInterest: model.closedProportionCarriedInterest,
      fundName: model.fundName,
      fundType: model.fundType ? model.fundType.codename : null,
      fundSubtype: model.fundSubtype ? model.fundSubtype.codename : null,
      isDueDiligence: model.isDueDiligence,
      isMarketplace: model.isMarketplace,
      inviteNow: model.inviteNow,
      inviteContactAsAdmin: model.inviteContactAsAdmin,
    };

    // Omitted on create. The backend assigns the default and rejects the field on POST.
    if (model.dataroomOpen) {
      dto.dataroomOpen = model.dataroomOpen;
    }

    let flatLegalRepresentatives: {} = null;
    let flatBeneficalOwners: {} = null;
    let deletedLegalRepresentatives: {} = null;
    let deletedBeneficalOwners: {} = null;

    if (model.legalRepresentatives) {
      flatLegalRepresentatives = flattenContacts('legalRepr', model.legalRepresentatives ?? []);
      deletedLegalRepresentatives = buildDeletedContacts(
        'legalRepr',
        model.legalRepresentatives?.length ?? 0,
        model.legalRepresentativesBackendCount ?? 0,
      );
    }

    if (model.beneficialOwners) {
      flatBeneficalOwners = flattenContacts('benefOwner', model.beneficialOwners ?? []);
      deletedBeneficalOwners = buildDeletedContacts(
        'benefOwner',
        model.beneficialOwners?.length ?? 0,
        model.beneficialOwnersBackendCount ?? 0,
      );
    }

    const image: {} = null;
    const result = Object.assign(
      dto,
      flatLegalRepresentatives,
      flatBeneficalOwners,
      deletedLegalRepresentatives,
      deletedBeneficalOwners,
      image,
    ) as CompanyWriteDTO;

    result.fundType = model.fundType ? model.fundType.codename : (result.fundType as any)?.codename || null;
    result.fundSubtype = model.fundSubtype ? model.fundSubtype.codename : (result.fundSubtype as any)?.codename || null;

    // Add snake_case fields for backend API
    if (model.type === 'emittent') {
      (result as any).invite_contact_as_admin = model.inviteContactAsAdmin ?? false;
      (result as any).invite_now = model.inviteNow ?? true; // Always true by default for emittent
    }

    return result;
  }

  public deserialize(dto: CompanyReadDTO): Company {
    const fos = new FileObjectSerializer();
    const vos = new ValueObjectSerializer();

    return Company.create({
      address: dto.address,
      articlesOfAssociationOrSocialContract: dto.articlesOfAssocOrContract
        ? fos.deserialize(dto.articlesOfAssocOrContract)
        : null,
      bankAccountNumber: dto.bankAccountNumber,
      beneficialOwnerIdCardScan: dto.benefOwnerIdCardScan ? fos.deserialize(dto.benefOwnerIdCardScan) : null,
      financialAuthorityLicense2: dto.financialAuthorityLicense2
        ? fos.deserialize(dto.financialAuthorityLicense2)
        : null,
      beneficialOwnerSignedDeclaration: dto.benefOwnerSignedDeclaration
        ? fos.deserialize(dto.benefOwnerSignedDeclaration)
        : null,
      beneficialOwners: dto.benefOwnerNum ? unflattenContacts('benefOwner', dto, dto.benefOwnerNum) : null,
      beneficialOwnersBackendCount: dto.benefOwnerNum ?? 0,
      commercialRegisterExtract: dto.commercialRegisterExtract ? fos.deserialize(dto.commercialRegisterExtract) : null,
      country: dto.country ? vos.deserialize(dto.country) : null,
      depotAccountNumber: dto.depotAccountNumber,
      email: dto.email,
      esgCompliance: dto.esgCompliance ? vos.deserialize(dto.esgCompliance) : null,
      verificationFeedback: dto.verificationFeedback,
      firstContactFirstName: dto.firstContactFirstName,
      firstContactLastName: dto.firstContactLastName,
      firstContactRole: dto.firstContactRole,
      firstContactSalutation: dto.firstContactSalutation ? vos.deserialize(dto.firstContactSalutation) : null,
      firstContactTitle: dto.firstContactTitle ? vos.deserialize(dto.firstContactTitle) : null,
      id: dto.uid,
      image: dto.image ? fos.deserialize(dto.image) : null,
      investorClasses: dto.investorClasses ? dto.investorClasses.map((ic) => vos.deserialize(ic)) : null,
      isManagingCompany: dto.isManagingCompany,
      isVehicleCompany: dto.isVehicleCompany,
      lastAnnualFinancialStatement: dto.lastAnnualFinStatement ? fos.deserialize(dto.lastAnnualFinStatement) : null,
      legalRepresentativeIdCardScan: dto.legalReprIdCardScan ? fos.deserialize(dto.legalReprIdCardScan) : null,
      legalRepresentativeSignedDeclaration: dto.legalReprSignedDeclaration
        ? fos.deserialize(dto.legalReprSignedDeclaration)
        : null,
      legalRepresentatives: dto.legalReprNum ? unflattenContacts('legalRepr', dto, dto.legalReprNum) : null,
      legalRepresentativesBackendCount: dto.legalReprNum ?? 0,
      lei: dto.lei,
      mobile: dto.mobile,
      name: dto.fullCompanyName,
      phone: dto.phone,
      proofDocument: dto.proofDocument ? fos.deserialize(dto.proofDocument) : null,
      proofNote: dto.proofNote,
      provenAt: dto.provenAt ? new Date(dto.provenAt) : null,
      provenBy: dto.provenBy,
      relatedProducts: this.relatedProductsToValueObject(dto.relatedProducts as Partial<ProductDTO>[]),
      shortDescription: dto.shortDescription,
      status: dto.status,
      transparencyRegister: dto.transparencyRegistry ? fos.deserialize(dto.transparencyRegistry) : null,
      structureChart: dto.structureChart ? fos.deserialize(dto.structureChart) : null,
      bankStatement: dto.bankStatement ? fos.deserialize(dto.bankStatement) : null,
      type: dto.type,
      website: dto.website,
      investorType: dto.investorType,
      partner: dto.partner,
      ownWhitelabel: dto.ownWhitelabel,
      clientAdviceLicence: dto.clientAdviceLicence,
      adviceFooterText: dto.adviceFooterText,

      openProportionCommissionUpfront: dto.openProportionCommissionUpfront,
      openProportionManagementFee: dto.openProportionManagementFee,
      openProportionCarriedInterest: dto.openProportionCarriedInterest,
      closedProportionCommissionUpfront: dto.closedProportionCommissionUpfront,
      closedProportionManagementFee: dto.closedProportionManagementFee,
      closedProportionCarriedInterest: dto.closedProportionCarriedInterest,

      fundName: dto.fundName,
      fundType: dto.fundType
        ? typeof dto.fundType === 'string'
          ? AssetClassValueObject.create({ codename: dto.fundType })
          : new AssetClassValueObjectSerializer().deserialize(dto.fundType)
        : null,
      fundSubtype: dto.fundSubtype
        ? typeof dto.fundSubtype === 'string'
          ? AssetClassValueObject.create({ codename: dto.fundSubtype })
          : new AssetClassValueObjectSerializer().deserialize(dto.fundSubtype)
        : null,
      isDueDiligence: dto.isDueDiligence,
      isMarketplace: dto.isMarketplace,
      dataroomOpen: dto.dataroomOpen,
    });
  }

  private relatedProductsToValueObject(products: Partial<ProductDTO>[]): ValueObject[] {
    return products?.map((p) => {
      return ValueObject.create({ name: p.productName, codename: p.uid });
    });
  }
}

@Model(new CompanySerializer())
export class Company extends Entity {
  // Basic info
  public address: string;
  public country: ValueObject;
  public email: string;
  public esgCompliance: ValueObject;
  public image: FileObject;
  public isManagingCompany: boolean;
  public isVehicleCompany: boolean;
  public clientAdviceLicence: boolean;
  public adviceFooterText: string;
  public mobile: string;
  public name: string;
  public phone: string;
  public shortDescription: string;
  public status: CompanyStatus;
  public type: CompanyType;
  public website: string;
  public investorType: CompanyInvestorType;
  public partner: string;
  public ownWhitelabel: boolean;

  // Bank accounts
  public bankAccountNumber: string;
  public depotAccountNumber: string;

  // Tax related
  public lei: string;

  // Company verification
  public articlesOfAssociationOrSocialContract: FileObject;
  public commercialRegisterExtract: FileObject;
  public verificationFeedback?: string;
  public lastAnnualFinancialStatement: FileObject;
  public proofDocument: FileObject;
  public proofNote: string;
  public provenAt: Date;
  public provenBy: string;
  public transparencyRegister: FileObject;
  public structureChart: FileObject;
  public bankStatement: FileObject;

  // First contact
  public firstContactSalutation: ValueObject;
  public firstContactTitle: ValueObject;
  public firstContactFirstName: string;
  public firstContactLastName: string;
  public firstContactRole: string;

  // Legal representative(s) verification
  public legalRepresentativeSignedDeclaration: FileObject;
  public legalRepresentativeIdCardScan: FileObject;

  // Beneficial owner(s) verification
  public beneficialOwnerSignedDeclaration: FileObject;
  public beneficialOwnerIdCardScan: FileObject;

  // Contacts
  public legalRepresentatives: ContactCopy[];
  public beneficialOwners: ContactCopy[];

  // Advisory Licence
  public financialAuthorityLicense2: FileObject;

  // Commission
  public openProportionCommissionUpfront: string;
  public openProportionManagementFee: string;
  public openProportionCarriedInterest: string;
  public closedProportionCommissionUpfront: string;
  public closedProportionManagementFee: string;
  public closedProportionCarriedInterest: string;

  /**
   * Cached value of the backend state for legal representatives amount
   *
   * Useful e.g. for computing which items should be marked as deleted in flattened legal representatives collection.
   */
  public legalRepresentativesBackendCount: number;

  /**
   * Cached value of the backend state for beneficial owners amount
   *
   * Useful e.g. for computing which items should be marked as deleted in flattened beneficial owners collection.
   */
  public beneficialOwnersBackendCount: number;

  // Optional fields
  public investorClasses?: ValueObject[];
  public relatedProducts?: ValueObject[];

  public fundName?: string;
  public fundType?: AssetClassValueObject;
  public fundSubtype?: AssetClassValueObject;
  public isDueDiligence?: boolean;
  public isMarketplace?: boolean;
  public inviteNow?: boolean;
  public inviteContactAsAdmin?: boolean;
  public dataroomOpen?: DataroomOpenType;

  public static create(data: Partial<Company>): Company {
    return Object.assign(new Company(), data);
  }

  public static createEmpty(): Company {
    return Object.assign(new Company(), {
      address: undefined,
      articlesOfAssociationOrSocialContract: undefined,
      bankAccountNumber: undefined,
      beneficialOwnerIdCardScan: undefined,
      beneficialOwnerSignedDeclaration: undefined,
      beneficialOwners: undefined,
      beneficialOwnersBackendCount: undefined,
      commercialRegisterExtract: undefined,
      country: undefined,
      depotAccountNumber: undefined,
      email: undefined,
      firstContactFirstName: undefined,
      firstContactLastName: undefined,
      firstContactRole: undefined,
      firstContactSalutation: undefined,
      firstContactTitle: undefined,
      id: undefined,

      investorClasses: undefined,
      isManagingCompany: undefined,
      isVehicleCompany: undefined,
      lastAnnualFinancialStatement: undefined,
      legalRepresentatives: undefined,
      legalRepresentativesBackendCount: undefined,
      legalRepresentativeIdCardScan: undefined,
      legalRepresentativeSignedDeclaration: undefined,
      lei: undefined,
      mobile: undefined,
      name: undefined,
      phone: undefined,
      shortDescription: undefined,
      transparencyRegister: undefined,
      structureChart: undefined,
      bankStatement: undefined,
      type: undefined,
      website: undefined,
      investorType: undefined,
      partner: undefined,
      ownWhitelabel: undefined,
      clientAdviceLicence: undefined,
      adviceFooterText: undefined,
      financialAuthorityLicense2: undefined,

      openProportionCommissionUpfront: undefined,
      openProportionManagementFee: undefined,
      openProportionCarriedInterest: undefined,
      closedProportionCommissionUpfront: undefined,
      closedProportionManagementFee: undefined,
      closedProportionCarriedInterest: undefined,

      fundName: undefined,
      fundType: undefined,
      fundSubtype: undefined,
      isDueDiligence: undefined,
      isMarketplace: undefined,
      dataroomOpen: undefined,
    });
  }

  public getInitials(): string {
    return this.name.charAt(0);
  }
}

function flattenContacts(prefix: ContactPrefix, contacts: ContactCopy[]): {} {
  const dto = {};

  contacts.forEach((contact, index) => {
    dto[`${prefix}N${index}Address`] = contact.address;
    dto[`${prefix}N${index}Email`] = contact.email;
    dto[`${prefix}N${index}FirstName`] = contact.firstName;
    dto[`${prefix}N${index}IsPep`] = contact.isPep;
    dto[`${prefix}N${index}LastName`] = contact.lastName;
    dto[`${prefix}N${index}Phone`] = contact.phone;
    dto[`${prefix}N${index}Salutation`] = contact.salutation?.codename ?? null;
    dto[`${prefix}N${index}Title`] = contact.title?.codename ?? null;
    dto[`${prefix}N${index}Type`] = contact.type;
    dto[`${prefix}N${index}SingedDeclaration`] = contact.signedDeclaration;
    dto[`${prefix}N${index}IdCardScan`] = contact.idCardScan;
  });

  return dto;
}

function unflattenContacts(prefix: ContactPrefix, dto: CompanyReadDTO, collectionSize: number): ContactCopy[] {
  const suffixes: ContactSuffix[] = [
    'FirstName',
    'IsPep',
    'LastName',
    'Address',
    'Email',
    'Mobile',
    'Phone',
    'Salutation',
    'Title',
    'SignedDeclaration',
    'IdCardScan',
  ];
  const collection = new Array(collectionSize);

  Object.keys(dto).forEach((key) => {
    if (key.startsWith(prefix) && suffixes.some((v) => key.endsWith(v))) {
      // Retrieve index in collection from key name. Keys are in '{prefix}N{0}{suffix}' form.
      // As number after 'N' can be of any length, use parseInt() ability to handle strings as an input.
      // `prefix.length + 1` is used to start parsing just after 'N' separator.
      const index: number = parseInt(key.substring(prefix.length + 1, key.length), 10);

      collection[index] = ContactCopy.create({
        address: dto[`${prefix}N${index}Address`] as string,
        email: dto[`${prefix}N${index}Email`] as string,
        firstName: dto[`${prefix}N${index}FirstName`] as string,
        isPep: dto[`${prefix}N${index}IsPep`] as boolean,
        lastName: dto[`${prefix}N${index}LastName`] as string,
        phone: dto[`${prefix}N${index}Phone`] as string,
        salutation: ValueObject.create(dto[`${prefix}N${index}Salutation`] as ValueObjectDTO),
        title: ValueObject.create(dto[`${prefix}N${index}Title`] as ValueObjectDTO),
        type: dto[`${prefix}N${index}Type`] as ContactTypeDTO,
      });
    }
  });

  return collection;
}

/**
 * Build an object of contacts to be deleted.
 *
 * To delete an item in a flattened backend collection `{ {prefix}N{0}Delete: true }` field(s) need to be passed in the request payload.
 */
function buildDeletedContacts(prefix: ContactPrefix, count: number, backendCount: number): {} {
  const deletedContacts = {};

  if (backendCount <= count) {
    return deletedContacts;
  }

  // needs to be filled with something so forEach() is invoked
  new Array(backendCount - count).fill('Thanks, Django!').forEach((_, index) => {
    index = index + count;
    deletedContacts[`${prefix}N${index}Delete`] = true;
  });

  return deletedContacts;
}
